import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Marker as MarkerSimpleMap } from 'react-simple-maps';

const CIRCLE = 'circle';
const RECTANGLE = 'rectangle';
const ELLIPSE = 'ellipse';

/**
 * Function returning function for dynamically calculating fill/color or stroke
 *
 * @type {function}
 * @param {*} prop function or static value for fill or stroke
 * @returns {function}
 */
const asFunc = (prop) => {
  if (typeof prop === 'function') return prop;
  return () => prop;
};

/**
 * A function that returns `Circle` component when shape is of type circle
 *
 * @type {function}
 * @param {data} data marker data for dynamically calculating radius
 * @param {radius} radius radius of a circle
 * @param {fill} fill color of a circle
 * @param {stroke} stroke border of a circle
 * @param {strokeWidth} strokeWidth border width of a circle
 * @returns {Component}
 */
const Circle = ({ data, radius, fill, stroke, strokeWidth }) => {
  return <circle r={asFunc(radius)(data)} fill={asFunc(fill)(data)} stroke={stroke} strokeWidth={strokeWidth} />;
};

/**
 * A function that returns `Rectangle` component when shape is of type rectangle
 *
 * @type {function}
 * @param {data} data marker data for dynamically calculating width and height
 * @param {width} width width of a rectangle
 * @param {height} height height of a rectangle
 * @param {fill} fill color of a rectangle
 * @param {stroke} stroke border of a rectangle
 * @param {strokeWidth} strokeWidth border width of a rectangle
 * @returns {Component}
 */
const Rectangle = ({ data, width, height, fill, stroke, strokeWidth }) => {
  return (
    <rect
      width={asFunc(width)(data)}
      height={asFunc(height)(data)}
      fill={asFunc(fill)(data)}
      stroke={stroke}
      strokeWidth={strokeWidth}
    />
  );
};

/**
 * A function that returns `Ellipse` component when shape is of type ellipse
 *
 * @type {function}
 * @param {data} data marker data for dynamically calculating horizontal and vertical radius
 * @param {rx} rx horizontal radius of a ellipse
 * @param {ry} ry vertical radius of a ellipse
 * @param {fill} fill color of a ellipse
 * @param {stroke} stroke border of a ellipse
 * @param {strokeWidth} strokeWidth border width of a ellipse
 * @returns {Component}
 */
const Ellipse = ({ data, rx, ry, fill, stroke, strokeWidth }) => {
  return (
    <ellipse
      rx={asFunc(rx)(data)}
      ry={asFunc(ry)(data)}
      fill={asFunc(fill)(data)}
      stroke={stroke}
      strokeWidth={strokeWidth}
    />
  );
};

/**
 * **Access:**
 * ```js
 * import { Marker } from '@iss/client-react-components';
 * ```
 *  A Marker component to place a marker on a map based on the coordinates.
 *  It provides flexibility to specify the shape of markers along with capability to define their color and size dynamically.
 */
export default class Marker extends Component {
  static propTypes = {
    /** Coordinates to place markers. **Note: It should be in [lon, lat] format.** */
    coordinates: PropTypes.arrayOf(PropTypes.number).isRequired,
    /** Type of shape for markers.*/
    shape: PropTypes.oneOf([CIRCLE, RECTANGLE, ELLIPSE]).isRequired,
    /** The radius of a circle if shape type is a circle. */
    radius: PropTypes.oneOfType([PropTypes.func, PropTypes.number, PropTypes.string]),
    /** Width of a rectangle when the shape is of type rectangle. */
    width: PropTypes.oneOfType([PropTypes.func, PropTypes.number, PropTypes.string]),
    /** Height of rectangle when the shape is of type rectangle. */
    height: PropTypes.oneOfType([PropTypes.func, PropTypes.number, PropTypes.string]),
    /** The horizontal radius of ecllipse when the shape is of type eclipse. */
    rx: PropTypes.oneOfType([PropTypes.func, PropTypes.number, PropTypes.string]),
    /** The vertical radius of ecllipse when the shape is of type eclipse. */
    ry: PropTypes.oneOfType([PropTypes.func, PropTypes.number, PropTypes.string]),
    /** Label for marker. */
    text: PropTypes.string,
    /** Border for marker. Provide a function for dynamically define the border.*/
    stroke: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    /** Specify border opacity for markers */
    strokeOpacity: PropTypes.number,
    /** Specify border width for markers */
    strokeWidth: PropTypes.number,
    /** Fill colors to marker. Provide a function for dynamically calculate the color of markers */
    fill: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    /** Specify color opacity/transparency for map.*/
    fillOpacity: PropTypes.number,
  };

  static defaultProps = {
    shape: CIRCLE,
    radius: 5,
    text: null,
    stoke: '#000000',
    strokeOpacity: 1,
    strokeWidth: 2,
    fill: '#808080',
    fillOpacity: 1,
  };

  render() {
    const { coordinates, shape, text } = this.props;

    let markerIcon;
    switch (shape) {
      case CIRCLE:
        markerIcon = Circle(this.props);
        break;
      case RECTANGLE:
        markerIcon = Rectangle(this.props);
        break;
      case ELLIPSE:
        markerIcon = Ellipse(this.props);
        break;
      default:
        console.warn('Invalid shape provided!');
    }

    return (
      <MarkerSimpleMap key={coordinates.join()} coordinates={coordinates}>
        {markerIcon}
        {text && <text textAnchor="middle">{text}</text>}
      </MarkerSimpleMap>
    );
  }
}

Marker.propTypes = {
  shape(props, propName, component) {
    if (props[propName] === RECTANGLE) {
      if (!(props['width'] && props['height'])) {
        return new Error(`width or height is missing for ${props[propName]} in ${component}. Validation failed.`);
      }
    } else if (props[propName] === ELLIPSE) {
      if (!(props['rx'] && props['ry'])) {
        return new Error(`rx or ry is missing for ${props[propName]} in ${component}. Validation failed.`);
      }
    }
  },
};
